import createPica from 'pica';

export interface ResizeDimensions {
  readonly width: number;
  readonly height: number;
}

export interface ResizeOptions extends ResizeDimensions {
  /** 是否強制使用高品質縮放（適用大圖） */
  readonly highQuality?: boolean;
}

const DEFAULT_PICA_QUALITY = 3;

const pica = createPica();

/**
 * 使用原生 Canvas 進行同步縮放。
 * 適合小圖或僅做少量調整時使用。
 */
export function resizeWithCanvas(
  source: HTMLCanvasElement,
  dimensions: ResizeDimensions
): HTMLCanvasElement {
  const target = document.createElement('canvas');
  target.width = dimensions.width;
  target.height = dimensions.height;

  const context = target.getContext('2d');
  if (!context) {
    throw new Error('無法建立畫布');
  }

  context.drawImage(
    source,
    0,
    0,
    source.width,
    source.height,
    0,
    0,
    dimensions.width,
    dimensions.height
  );
  return target;
}

/**
 * 使用 pica 進行高品質縮放。
 * 內部會建立新的 Canvas 並回傳。
 */
export async function resizeWithPica(
  source: HTMLCanvasElement,
  dimensions: ResizeDimensions
): Promise<HTMLCanvasElement> {
  const target = document.createElement('canvas');
  target.width = dimensions.width;
  target.height = dimensions.height;

  const result = await pica.resize(source, target, {
    quality: DEFAULT_PICA_QUALITY,
    alpha: true,
  });

  return result;
}

/**
 * 根據輸入尺寸與是否為大圖，決定使用哪一種縮放實作。
 *
 * - 預設情況下，小圖直接使用 Canvas，速度較快。
 * - 大圖或明確要求 highQuality 時使用 pica，以獲得較佳畫質。
 */
export async function resizeCanvas(
  source: HTMLCanvasElement,
  options: ResizeOptions
): Promise<HTMLCanvasElement> {
  const { width, height, highQuality } = options;
  const isLargeImage = source.width * source.height >= 1920 * 1080;

  if (highQuality ?? isLargeImage) {
    return resizeWithPica(source, { width, height });
  }

  return resizeWithCanvas(source, { width, height });
}
